import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { optToPromise } from '@commons/util/utilities/option';
import { IDeferred, IPromise, IQService, isDefined } from 'angular';

import { ChannelDirection, None, Option, ToastClass, ToastType } from '@contact/upgrade';

import { DEFAULT_TOAST_DURATION, EpticaHelper, REJECTION_PENDING_ASK_FOR_HELP } from './eptica.helper';
import { IEpticaJsonResponse } from './models/eptica.request.interfaces';
import { ServiceWrapper } from './models/service-wrapper';

export class EpticaHelperJson {
  static sendAndCloseClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let action = '/eptica_ias/serviceRequest/answerRequest.action';
    let form = 'formAnswerRequest';
    let validateFn = () => EpticaHelperJson.validateForm(sw, frame, action, form);
    return this.chkAskHelpAndProcessForm(sw, frame, action, form, validateFn);
  }

  static sendOutgoingClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let action = '/eptica_ias/serviceRequest/createRequest.action';
    let form = 'createRequest';
    let validateFn = () => EpticaHelperJson.validateForm(sw, frame, action, form);
    return this.processForm(sw, frame, action, form, validateFn);
  }

  static sendAndDeferClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let action = '/eptica_ias/serviceRequest/answerRequestAndPostpone.action';
    let form = 'formAnswerRequest';
    let validateFn = () => EpticaHelperJson.validateForm(sw, frame, action, form);
    return this.processForm(sw, frame, action, form, validateFn);
  }

  static sendPostponeOutgoingClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let action = '/eptica_ias/serviceRequest/createRequestAndPostpone.action';
    let form = 'createRequest';
    let validateFn = () => EpticaHelperJson.validateForm(sw, frame, action, form);
    return this.processForm(sw, frame, action, form, validateFn);
  }

  static saveDraftClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let action = '/eptica_ias/serviceDraft/saveDraft.action';
    let form = 'formAnswerRequest';
    let validateFn = () => EpticaHelperJson.validateForm(sw, frame, action, form);
    return this.processForm(sw, frame, action, form, validateFn);
  }

  static saveDraftForNewClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let action = '/eptica_ias/serviceDraft/saveDraftForNewRequest.action';
    let form = 'createRequest';
    let validateFn = () => EpticaHelperJson.validateForm(sw, frame, action, form);
    return this.processForm(sw, frame, action, form, validateFn);
  }

  static closeClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    return this.chkAskHelpAndProcessForm(sw, frame, '/eptica_ias/serviceRequest/closeRequest.action', 'closeRequestForm');
  }

  static delegateClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let promise = EpticaHelperJson.waitDropzoneEvents(sw, frame, '#delegateRequest');
    EpticaHelper.simulateClick(frame, 'delegateRequestBtn');
    return promise;
  }

  static validateForm(sw: ServiceWrapper, frame: HTMLIFrameElement, action: string, form: string): boolean {
    return EpticaHelper.callEpticaFn<boolean>(frame, sw.$log, 'validateForm', action, `#${form}`).getOrElse(true);
  }

  static processForm(
    sw: ServiceWrapper,
    frame: HTMLIFrameElement,
    action: string,
    form: string,
    validateFn: () => boolean = () => true
  ): IPromise<IEpticaJsonResponse> {
    let promise = EpticaHelperJson.waitDropzoneEvents(sw, frame, `#${form}`);
    if (validateFn()) {
      EpticaHelper.callEpticaFn(frame, sw.$log, 'processForm', action, `#${form}`);
    }
    return promise;
  }

  static waitDropzoneEvents(sw: ServiceWrapper, frame: HTMLIFrameElement, form: string): IPromise<IEpticaJsonResponse> {
    let deferred = sw.$q.defer<IEpticaJsonResponse>();
    let Dropzone = frame.contentWindow!['Dropzone'];
    Dropzone.forElement(form)
      .on('error', (_, serverResponse) => EpticaHelperJson.reject(deferred, serverResponse))
      .on('successmultiple', (_, serverResponse) => EpticaHelperJson.resolve(deferred, serverResponse));
    return deferred.promise;
  }

  static chkAskHelpAndProcessForm(
    sw: ServiceWrapper,
    frame: HTMLIFrameElement,
    action: string,
    form: string,
    validateFn: () => boolean = () => true
  ) {
    if (validateFn()) {
      let submitForm = (deferred: IDeferred<IEpticaJsonResponse>) =>
        this.processForm(sw, frame, action, form).then(
          result => {
            deferred.resolve(result);
            return result;
          },
          error => {
            deferred.reject(error);
            return sw.$q.reject(error);
          }
        );
      return EpticaHelperJson.checkPendingAskForHelpAndSubmit(
        sw,
        frame,
        EpticaHelper.getEpticaElementById(frame, form) as HTMLFormElement,
        submitForm
      );
    } else {
      return sw.$q.reject('unvalid form');
    }
  }

  static checkPendingAskForHelpAndSubmit(
    sw: ServiceWrapper,
    frame: HTMLIFrameElement,
    form: HTMLFormElement,
    submitForm: (deferred: IDeferred<IEpticaJsonResponse>) => IPromise<IEpticaJsonResponse>,
    askForHelpCancellationComment = ' '
  ): IPromise<IEpticaJsonResponse> {
    const deferred = sw.$q.defer<IEpticaJsonResponse>();
    // tslint:disable:only-arrow-functions
    let askForHelpCancellationCallback = function () {
      $(form).find('input[name="askForHelpCancellationComment"]').val(askForHelpCancellationComment);
      $(form).find('input[name="cancelPendingAskForHelp"]').val('true');
      submitForm(deferred);
    };
    // tslint:enable

    (frame.contentWindow as any).checkForPendingAskForHelp(function presenceCallback(result) {
      if (result) {
        askForHelpCancellationCallback();
      } else {
        submitForm(deferred);
      }
    });
    /**
                checkForPendingAskForHelp(
                    function presenceCallback(val) {
                        alert('presence ['+val+']');
                    },
                    function absenceCallback() {
                        alert('absence');
                    }
                );
        */

    return deferred.promise;
  }

  static assistClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let promise = EpticaHelperJson.waitDropzoneEvents(sw, frame, '#createAskForHelp');
    EpticaHelper.simulateClick(frame, 'createAskForHelp_commonRequest_send');
    return promise;
  }

  static askForReview(sw: ServiceWrapper, frame: HTMLIFrameElement, comment: string): IPromise<IEpticaJsonResponse> {
    let commentFormField: HTMLInputElement = EpticaHelper.getEpticaElementById(
      frame,
      'formAnswerRequest_askForReviewComment'
    ) as HTMLInputElement;
    if (commentFormField) {
      commentFormField.value = comment;
    }
    return this.askForReviewClick(sw, frame);
  }

  static askForReviewClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let action = '/eptica_ias/serviceRequest/askForReview.action';
    let form = 'formAnswerRequest';
    let validateFn = () => EpticaHelperJson.validateForm(sw, frame, action, form);
    return EpticaHelperJson.chkAskHelpAndProcessForm(sw, frame, action, form, validateFn);
  }

  static acceptApprovalClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let action = '/eptica_ias/serviceRequest/acceptReview.action';
    let form = 'formAnswerRequest';
    let validateFn = () => EpticaHelperJson.validateForm(sw, frame, action, form);
    return this.processForm(sw, frame, action, form, validateFn);
  }

  static rejectApprovalClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    const action = '/eptica_ias/serviceRequest/rejectReview.action';
    const form = 'formAnswerRequest';
    let comment = '';

    const commentInput = EpticaHelper.getEpticaElementById(frame, 'rejectReview_promptField') as HTMLInputElement;
    if (!!commentInput) {
      comment = commentInput.value;
    } else {
      // eptica >= 9.6
      const commentElement = EpticaHelper.getEpticaElementById(frame, 'rejectReview_rejectReview_comment') as HTMLTextAreaElement;
      comment = commentElement ? commentElement.value : '';
    }

    EpticaHelper.epticaJQuery(frame)('#formAnswerRequest', frame.contentDocument).find('input[name="rejectReviewComment"]').val(comment);
    return this.processForm(sw, frame, action, form);
  }

  static askForHelpAnswerClick(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    const action = '/eptica_ias/serviceRequest/answerAskForHelp.action';
    const form = 'answerAskForHelp';
    const validateFn = () => EpticaHelperJson.validateForm(sw, frame, action, form);
    return this.processForm(sw, frame, action, form, validateFn);
  }

  static submitWithAttachmentsAndWaitForLoad(
    sw: ServiceWrapper,
    frame: HTMLIFrameElement,
    formId: string,
    action: string
  ): IPromise<IEpticaJsonResponse> {
    const dropZone = (frame.contentWindow as any).Dropzone.forElement('.dropzone');
    if (dropZone.getQueuedFiles().length === 0) {
      return EpticaHelperJson.submitFormAndWailtForLoad(sw, frame, formId);
    } else {
      dropZone.options.url = EpticaHelper.getContextPath(frame) + action;
      let promiseEpticaData = EpticaHelperJson.wrapEpticaHandleFileSubmitMethodAndWait(sw.$q, frame);
      dropZone.processQueue();
      return promiseEpticaData;
    }
  }

  static submitFormAndWailtForLoad(sw: ServiceWrapper, frame: HTMLIFrameElement, formElementId: string): IPromise<IEpticaJsonResponse> {
    EpticaHelper.submitEpticaForm(frame, formElementId);
    return EpticaHelperJson.waitForEpticaLoad(sw, frame);
  }

  static waitForEpticaLoad(
    sw: ServiceWrapper,
    frame: HTMLIFrameElement,
    checkFunc: (frame: HTMLIFrameElement) => boolean = () => true
  ): IPromise<IEpticaJsonResponse> {
    return EpticaHelper.waitForLoad(sw.$q, frame, checkFunc).then(f => EpticaHelperJson.doPostLoadActions(sw, f));
  }

  static confirmClosePendingAssist(submitForm: () => IPromise<IEpticaJsonResponse>): IPromise<IEpticaJsonResponse> {
    return submitForm();
  }

  static doPostLoadActions(sw: ServiceWrapper, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let optEpticaJsonResponse = EpticaHelperJson.getContent(frame);
    if (optEpticaJsonResponse.isDefined()) {
      EpticaHelperJson.displayActionMessages(sw.toastDeprecatedService, optEpticaJsonResponse.get());
      EpticaHelper.addPrintAndPreviewButtons(sw.$i18next, sw.sessionDeprecatedService, frame);
      EpticaHelper.customizeAnswerDisplay(frame, sw.$i18next, sw.sessionDeprecatedService, ChannelDirection.INCOMING);
    }
    return optToPromise(optEpticaJsonResponse, sw.$q);
  }

  static wrapEpticaHandleFileSubmitMethodAndWait($q: IQService, frame: HTMLIFrameElement): IPromise<IEpticaJsonResponse> {
    let epticaWindow = frame.contentWindow as any;
    if (!isDefined(epticaWindow.oldHandleFileSubmit)) {
      epticaWindow.oldHandleFileSubmit = epticaWindow.handleFileSubmit;
    }

    let defered = $q.defer<IEpticaJsonResponse>();
    epticaWindow.handleFileSubmit = (file, statusMessageClass) => {
      let xhr = file ? (file[0] ? file[0].xhr : null) : null;
      if (!xhr) {
        defered.reject('xhr is null');
      }
      if (xhr.stzatus === 200) {
        epticaWindow.oldHandleFileSubmit(file, statusMessageClass);
        return optToPromise(EpticaHelperJson.getContent(frame), $q);
      } else {
        epticaWindow.oldHandleFileSubmit(file, statusMessageClass);
        defered.reject('xhr status is ' + xhr.status);
        return Promise.resolve();
      }
    };
    return defered.promise;
  }

  static getContent(frame: HTMLIFrameElement): Option<IEpticaJsonResponse> {
    return EpticaHelperJson.parseResponse($(frame).contents().text());
  }

  static parseResponse(epticaServerResponse: any): Option<IEpticaJsonResponse> {
    // eptica < 9.7
    if (typeof epticaServerResponse === 'string') {
      try {
        const response: IEpticaJsonResponse = JSON.parse(JSON.parse(epticaServerResponse));
        return Option.of(response);
      } catch (e) {
        return None;
      }
    } else {
      // eptica >= 9.7, not a string
      try {
        if (typeof epticaServerResponse === 'object') {
          const response: IEpticaJsonResponse = epticaServerResponse;
          return Option.of(response);
        } else {
          return None;
        }
      } catch (e) {
        return None;
      }
    }
  }

  static parseGlobalResponse(epticaServerResponse: string): Option<IEpticaJsonResponse[]> {
    try {
      let responses: any = JSON.parse(epticaServerResponse);
      if (responses.global && responses.global.length > 0) {
        let epticaJsonResponseArray: IEpticaJsonResponse[] = [];
        for (let rawJson of responses.global) {
          epticaJsonResponseArray.push(JSON.parse(rawJson));
        }
        return Option.of(epticaJsonResponseArray);
      } else {
        return None;
      }
    } catch (e) {
      return None;
    }
  }

  static resolve(deferred: IDeferred<IEpticaJsonResponse>, serverResponse: string): void {
    let response = EpticaHelperJson.parseResponse(serverResponse);
    if (response.isDefined()) {
      deferred.resolve(response.get());
    } else {
      deferred.reject('');
    }
  }

  static reject(deferred: IDeferred<IEpticaJsonResponse>, serverResponse: string): void {
    let response = EpticaHelperJson.parseGlobalResponse(serverResponse);
    if (response.isDefined()) {
      deferred.reject(response.get());
    } else {
      deferred.reject('');
    }
  }

  /**
   * Display eptica message with toats
   */
  static displayActionMessages(toastDeprecatedService: ToastDeprecatedService, epticaJson: IEpticaJsonResponse): void {
    //BNPPFCUS-1600
    /*
     * on supprime le client du label provenant d'Eptica, car si changement de destinataire dans le mail, on est pas encore synchro avec Eptica
     * donc Eptica nous renvoie le label avec le premier destinataire
     * Your answer has been sent successfully to customer XXX
     * Votre réponse au client XXX a bien été transmise
     */
    if (epticaJson) {
      let messageToDisplay = epticaJson.messageLabel;
      if (messageToDisplay.indexOf('Your answer has been sent successfully to customer') >= 0) {
        messageToDisplay = 'Your answer has been sent successfully to customer';
      } else if (messageToDisplay.indexOf('Votre réponse au client ') >= 0 && messageToDisplay.indexOf('a bien été transmise') >= 0) {
        messageToDisplay = 'Votre réponse au client a bien été transmise';
      }
      if (epticaJson.messageState === 'success' && epticaJson.messageLabel) {
        toastDeprecatedService.add(ToastType.NOTIFICATION, messageToDisplay, ToastClass.MAIL, DEFAULT_TOAST_DURATION);
      } else if (epticaJson.messageState === 'error' && epticaJson.messageLabel) {
        toastDeprecatedService.alert(messageToDisplay);
      }
    }
  }

  static simulateClickAndAttachmentsAndLoad(
    sw: ServiceWrapper,
    frame: HTMLIFrameElement,
    elementId: string,
    forceNoAttachment: boolean = false
  ): IPromise<IEpticaJsonResponse> {
    // TODO 6958 forceNoAttachment remove this condition and add attachment zone in transfer view
    let dropZone = (frame.contentWindow as any).Dropzone.forElement('.dropzone');
    if (forceNoAttachment || dropZone.getQueuedFiles().length === 0) {
      return EpticaHelperJson.simulateClickAndWaitForLoad(sw, frame, elementId);
    } else {
      let promiseEpticaData = EpticaHelperJson.wrapEpticaHandleFileSubmitMethodAndWait(sw.$q, frame);
      EpticaHelper.simulateClick(frame, elementId);
      return promiseEpticaData;
    }
  }

  static simulateClickAndWaitForLoad(sw: ServiceWrapper, frame: HTMLIFrameElement, elementId: string): IPromise<IEpticaJsonResponse> {
    EpticaHelper.simulateClick(frame, elementId);
    return EpticaHelperJson.waitForEpticaLoad(sw, frame);
  }

  static _formAnswerCheckPendingAskForHelpAndSubmitReviewForm(
    sw: ServiceWrapper,
    frame: HTMLIFrameElement,
    action: string
  ): IPromise<IEpticaJsonResponse> {
    let form: HTMLFormElement = EpticaHelper.getEpticaElementById(frame, 'formAnswerRequest') as HTMLFormElement;
    let submitForm = (deferred: IDeferred<IEpticaJsonResponse>) => {
      // copy field comment value in hidden field
      let hiddenCommentInput: HTMLInputElement = EpticaHelper.getEpticaElementBySelector(
        frame,
        'input[name=askForReviewComment]'
      ) as HTMLInputElement;
      let commentInput: HTMLInputElement = EpticaHelper.getEpticaElementById(frame, 'askForReview_promptField') as HTMLInputElement;
      hiddenCommentInput.value = commentInput.value;
      form.target = '_self';
      form.action = EpticaHelper.getContextPath(frame) + action;
      deferred.resolve(EpticaHelperJson.submitWithAttachmentsAndWaitForLoad(sw, frame, form.id, action));
    };
    return EpticaHelperJson.checkPendingAskForHelpAndSubmitReviewForm(sw, frame, form, submitForm);
  }

  static checkPendingAskForHelpAndSubmitReviewForm(
    sw: ServiceWrapper,
    frame: HTMLIFrameElement,
    form: HTMLFormElement,
    submitForm: (deferred: IDeferred<IEpticaJsonResponse>) => void
  ): IPromise<IEpticaJsonResponse> {
    let deferred = sw.$q.defer<IEpticaJsonResponse>();
    // tslint:disable:only-arrow-functions
    let askForHelpCancellationCallback = function (cancellationComment) {
      $(form).find('input[name="askForHelpCancellationComment"]').val(cancellationComment);
      $(form).find('input[name="cancelPendingAskForHelp"]').val('true');
      submitForm(deferred);
    };
    // tslint:enable

    (frame.contentWindow as any).checkForPendingAskForHelp(
      function presenceCallback() {
        (frame.contentWindow as any).warnAboutPendingAskForHelp(askForHelpCancellationCallback);
        deferred.reject({
          cause: REJECTION_PENDING_ASK_FOR_HELP,
          commentFieldId: 'askForReview_promptField',
          confirmButtonId: 'askForHelpCancellation_promptPopupValidateButton',
        });
      },
      () => submitForm(deferred)
    );

    return deferred.promise;
  }
}
